import { DestroyRef, inject, Injectable, isDevMode, NgZone, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

import { BUILD_INFO } from '../build-info/build-info.token';
import { VERSION_CHECK_CONFIG, VERSION_RELOAD_BLOCKER, VersionManifest } from './version-check.model';

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly router = inject(Router);
  private readonly buildInfo = inject(BUILD_INFO);
  private readonly config = inject(VERSION_CHECK_CONFIG);
  private readonly reloadBlocked = inject(VERSION_RELOAD_BLOCKER);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _updateAvailable = signal(false);
  private readonly _showIdleBanner = signal(false);

  readonly updateAvailable = this._updateAvailable.asReadonly();
  readonly showIdleBanner = this._showIdleBanner.asReadonly();

  private lastCheckTime = 0;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  init(): void {
    if (!isPlatformBrowser(this.platformId) || isDevMode()) return;

    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        void this.checkOnNavigation(event.url);
        this.resetIdleTimer();
      });

    if (this.config.enableIdleBanner) {
      this.setupIdleDetection();
    }
  }

  forceReload(url?: string): void {
    window.location.assign(url ?? window.location.href);
  }

  private async checkOnNavigation(targetUrl: string): Promise<void> {
    const now = Date.now();

    if (now - this.lastCheckTime < this.config.throttleMs) return;

    this.lastCheckTime = now;

    const manifest = await this.fetchServerVersion();

    if (!manifest) return;

    if (manifest.commit !== this.buildInfo.commit) {
      this._updateAvailable.set(true);
      if (this.reloadBlocked()) {
        this._showIdleBanner.set(true);
        return;
      }

      this.forceReload(new URL(targetUrl, window.location.origin).href);
    }
  }

  private async fetchServerVersion(): Promise<VersionManifest | null> {
    try {
      const response = await fetch(this.config.versionUrl, { cache: 'no-store' });

      if (!response.ok) return null;

      return (await response.json()) as VersionManifest;
    } catch {
      return null;
    }
  }

  private setupIdleDetection(): void {
    this.ngZone.runOutsideAngular(() => {
      const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

      for (const event of events) {
        // zone.js reuses the first document listener's options: passive keydown would void editor preventDefault.
        document.addEventListener(event, () => this.resetIdleTimer(), {
          passive: event === 'touchstart' || event === 'scroll',
        });
      }

      this.resetIdleTimer();
    });
  }

  private resetIdleTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    this.idleTimer = setTimeout(() => {
      void this.checkIdleVersion();
    }, this.config.idleBannerMs);
  }

  private async checkIdleVersion(): Promise<void> {
    const manifest = await this.fetchServerVersion();

    if (!manifest) return;

    if (manifest.commit !== this.buildInfo.commit) {
      this.ngZone.run(() => {
        this._showIdleBanner.set(true);
      });
    }
  }
}
